import { useEffect, useState } from "react";
import { authApi, api } from "@/lib/api";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  Download,
  Clock,
  CalendarDays,
  Gauge,
  History,
  ArrowRight,
  RefreshCw,
  XCircle,
  UserPlus,
  BellRing,
  Printer,
} from "lucide-react";

function Stat({ label, value, sub }) {
  return (
    <div className="rounded-2xl border border-[#8E1226]/12 p-6">
      <div className="text-4xl font-serif text-[#8E1226]">{value}</div>
      <div className="mt-1 text-xs uppercase tracking-[0.18em] text-[#6D726A]">{label}</div>
      {sub && <div className="mt-2 text-sm text-[#6D726A]">{sub}</div>}
    </div>
  );
}

function FillBar({ pct }) {
  const color = pct >= 100 ? "#8E1226" : pct >= 50 ? "#0E6B3A" : "#0E6B3A";
  return (
    <div className="w-full h-2 rounded-full bg-[#8E1226]/10 overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${Math.min(pct, 100)}%`, background: color }} />
    </div>
  );
}

const EVENT_META = {
  signup: { icon: UserPlus, label: "Signed up", color: "#8E1226" },
  switch: { icon: RefreshCw, label: "Changed shift", color: "#0E6B3A" },
  cancel: { icon: XCircle, label: "Cancelled", color: "#083D21" },
};

function slotText(s) {
  if (!s) return "—";
  return `${s.role} · ${s.day_label} ${s.start}–${s.end}`;
}

function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleString("en-IE", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function ReportsPanel({ token }) {
  const [report, setReport] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [daysBefore, setDaysBefore] = useState("2");
  const [savingSettings, setSavingSettings] = useState(false);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await authApi(token).put("/admin/settings", { reminder_days_before: Number(daysBefore) });
      toast.success(
        Number(daysBefore) === 0
          ? "Reminders will now go out on the day of each shift."
          : `Reminders will now go out ${daysBefore} day${Number(daysBefore) === 1 ? "" : "s"} before each shift.`
      );
    } catch {
      toast.error("Could not save the reminder timing.");
    } finally {
      setSavingSettings(false);
    }
  };

  const printRota = async () => {
    setPrinting(true);
    try {
      const client = authApi(token);
      const [{ data: signupsData }, { data: shiftsData }] = await Promise.all([
        client.get("/admin/signups"),
        api.get("/shifts"),
      ]);
      const bySlot = {};
      (signupsData.signups || []).forEach((s) => {
        (bySlot[s.shift_id] = bySlot[s.shift_id] || []).push(s);
      });
      const esc = (v) =>
        String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      const printed = new Date().toLocaleString("en-IE", {
        weekday: "long", day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
      });

      const daysHtml = (shiftsData.days || [])
        .map((day, di, arr) => {
          const rolesHtml = (day.roles || [])
            .map((r) => {
              const rows = (r.slots || [])
                .map((sl) => {
                  const people = bySlot[sl.id] || [];
                  const names = people.length
                    ? people
                        .map(
                          (p) =>
                            `<div class="person"><strong>${esc(p.name)}</strong>${
                              p.phone ? ` · ${esc(p.phone)}` : ""
                            }${p.notes ? ` <span class="note">(${esc(p.notes)})</span>` : ""}</div>`
                        )
                        .join("")
                    : `<span class="empty">— no one signed up yet —</span>`;
                  return `<tr>
                    <td class="time">${esc(sl.start)} – ${esc(sl.end)}</td>
                    <td>${names}</td>
                    <td class="count">${people.length}/${sl.capacity}</td>
                  </tr>`;
                })
                .join("");
              return `<h3>${esc(r.role)}</h3>
                <table>
                  <thead><tr><th class="time">Time</th><th>Volunteers</th><th class="count">Booked</th></tr></thead>
                  <tbody>${rows}</tbody>
                </table>`;
            })
            .join("");
          return `<section${di < arr.length - 1 ? ' style="page-break-after: always;"' : ""}>
            <h2>${esc(day.label)} <span class="date">${esc(day.weekday)} · ${esc(day.date)}</span></h2>
            ${rolesHtml}
          </section>`;
        })
        .join("");

      const html = `<!doctype html><html><head><meta charset="utf-8">
        <title>Volunteer Rota — Meals on Wheels Tralee</title>
        <style>
          body { font-family: Georgia, 'Times New Roman', serif; color: #1a1a1a; margin: 32px; }
          h1 { font-size: 26px; margin: 0; }
          .sub { color: #555; font-size: 13px; margin: 6px 0 28px; }
          h2 { font-size: 21px; border-bottom: 2.5px solid #1a1a1a; padding-bottom: 6px; margin: 0 0 18px; }
          h2 .date { font-weight: normal; font-size: 14px; color: #555; margin-left: 10px; }
          h3 { font-size: 15px; text-transform: uppercase; letter-spacing: 0.08em; margin: 22px 0 8px; }
          table { width: 100%; border-collapse: collapse; font-size: 14px; }
          th { text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: #555; border-bottom: 1.5px solid #999; padding: 4px 8px; }
          td { border-bottom: 1px solid #ccc; padding: 6px 8px; vertical-align: top; }
          .time { width: 150px; white-space: nowrap; }
          .count { width: 70px; text-align: center; }
          .person { padding: 2px 0; }
          .note, .empty { color: #777; }
          footer { margin-top: 32px; font-size: 12px; color: #555; border-top: 1px solid #999; padding-top: 10px; }
        </style></head>
        <body>
          <h1>Meals on Wheels Tralee — Christmas Volunteer Rota</h1>
          <p class="sub">Printed ${esc(printed)}</p>
          ${daysHtml}
          <footer>Meals on Wheels Tralee · 066-7165071 · mealsonwheelstralee@gmail.com · Charity No. 20158316</footer>
        </body></html>`;

      const w = window.open("", "_blank");
      if (!w) {
        toast.error("Your browser blocked the print window — please allow pop-ups for this page.");
        return;
      }
      w.document.write(html);
      w.document.close();
      w.focus();
      w.print();
    } catch {
      toast.error("Could not build the rota.");
    } finally {
      setPrinting(false);
    }
  };

  const sendReminders = async () => {
    setSendingReminders(true);
    try {
      const { data } = await authApi(token).post("/admin/send-reminders");
      if (data.sent > 0) {
        toast.success(`Sent ${data.sent} reminder email${data.sent === 1 ? "" : "s"}.`);
        setReport((r) => (r ? { ...r, reminders_sent: (r.reminders_sent || 0) + data.sent } : r));
      } else {
        toast.info(
          Number(daysBefore) === 0
            ? "No reminders due right now (reminders go out on the day of each shift)."
            : `No reminders due right now (reminders go out ${daysBefore} day${Number(daysBefore) === 1 ? "" : "s"} before each shift).`
        );
      }
    } catch {
      toast.error("Could not send reminders.");
    } finally {
      setSendingReminders(false);
    }
  };

  useEffect(() => {
    const client = authApi(token);
    Promise.all([client.get("/admin/reports"), client.get("/admin/activity"), client.get("/admin/settings")])
      .then(([r, a, s]) => {
        setReport(r.data);
        setEvents(a.data.events || []);
        setDaysBefore(String(s.data.reminder_days_before ?? 2));
      })
      .catch(() => toast.error("Could not load reports."))
      .finally(() => setLoading(false));
  }, [token]);

  const downloadCsv = () => {
    if (!report) return;
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const L = [];
    L.push(esc("Meals on Wheels Tralee — Volunteer Report"));
    L.push("");
    L.push([esc("Total sign-ups"), report.total_signups].join(","));
    L.push([esc("Unique volunteers"), report.unique_volunteers].join(","));
    L.push([esc("Total volunteer hours"), report.total_hours].join(","));
    L.push([esc("Shift changes"), report.switches].join(","));
    L.push([esc("Cancellations"), report.cancellations].join(","));
    L.push([esc("Overall fill %"), report.fill.pct].join(","));
    L.push("");
    L.push(["Day", "Date", "Sign-ups", "Volunteers", "Hours"].map(esc).join(","));
    report.by_day.forEach((d) => L.push([d.day_label, d.date, d.signups, d.volunteers, d.hours].map(esc).join(",")));
    L.push("");
    L.push(["Job / Role", "Sign-ups", "Volunteers", "Hours"].map(esc).join(","));
    report.by_role.forEach((r) => L.push([r.role, r.signups, r.volunteers, r.hours].map(esc).join(",")));
    L.push("");
    L.push(["Signed up on (date)", "Count"].map(esc).join(","));
    report.signups_by_date.forEach((d) => L.push([d.date, d.count].map(esc).join(",")));
    L.push("");
    L.push(["Day", "Role", "Time", "Booked", "Capacity", "Remaining", "Fill %"].map(esc).join(","));
    report.shifts_fill.forEach((s) =>
      L.push([s.day_label, s.role, s.time, s.taken, s.capacity, s.remaining, s.pct].map(esc).join(","))
    );
    const blob = new Blob(["\uFEFF" + L.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mowt-volunteer-report-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-3 text-[#6D726A] py-10">
        <Loader2 className="w-5 h-5 animate-spin" /> Building reports…
      </div>
    );
  }
  if (!report) return null;

  const dayCounts = report.by_day.map((d) => `${d.day_label}: ${d.volunteers}`).join("  ·  ");

  return (
    <div data-testid="admin-reports-panel">
      <div className="flex items-center justify-between mb-8">
        <p className="text-[#6D726A] text-sm max-w-xl">
          A live summary of who volunteered, when, total hours, and which shifts still need people.
        </p>
        <div className="flex items-center gap-3">
          <button
            data-testid="print-rota-btn"
            onClick={printRota}
            disabled={printing}
            className="inline-flex items-center gap-2 rounded-full bg-[#0E6B3A] text-[#FBF7F0] px-5 py-2.5 text-sm font-medium hover:bg-[#083D21] transition-colors disabled:opacity-50"
          >
            {printing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" strokeWidth={1.7} />}
            Print rota
          </button>
          <button
            data-testid="send-reminders-btn"
            onClick={sendReminders}
            disabled={sendingReminders}
            className="inline-flex items-center gap-2 rounded-full border border-[#8E1226]/20 px-5 py-2.5 text-sm text-[#8E1226] hover:border-[#0E6B3A] hover:text-[#0E6B3A] transition-colors disabled:opacity-50"
          >
            {sendingReminders ? <Loader2 className="w-4 h-4 animate-spin" /> : <BellRing className="w-4 h-4" strokeWidth={1.7} />}
            Send reminders now
          </button>
          <button
            data-testid="report-export-btn"
            onClick={downloadCsv}
            disabled={report.total_signups === 0}
            className="inline-flex items-center gap-2 rounded-full border border-[#8E1226]/20 px-5 py-2.5 text-sm text-[#8E1226] hover:border-[#0E6B3A] hover:text-[#0E6B3A] transition-colors disabled:opacity-40"
          >
            <Download className="w-4 h-4" strokeWidth={1.7} /> Download report (CSV)
          </button>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-4 rounded-2xl border border-[#8E1226]/12 px-6 py-4" data-testid="reminder-settings">
        <BellRing className="w-4 h-4 text-[#0E6B3A]" strokeWidth={1.7} />
        <span className="text-sm text-[#8E1226] font-medium">Reminder emails go out</span>
        <Select value={daysBefore} onValueChange={setDaysBefore}>
          <SelectTrigger data-testid="reminder-days-select" className="w-44 border-[#8E1226]/20">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">On the day itself</SelectItem>
            {[1, 2, 3, 4, 5, 6, 7].map((n) => (
              <SelectItem key={n} value={String(n)}>
                {n} day{n === 1 ? "" : "s"} before
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-sm text-[#6D726A]">each shift</span>
        <button
          data-testid="reminder-days-save"
          onClick={saveSettings}
          disabled={savingSettings}
          className="inline-flex items-center gap-2 rounded-full bg-[#0E6B3A] text-[#FBF7F0] px-5 py-2 text-sm font-medium hover:bg-[#083D21] transition-colors disabled:opacity-50"
        >
          {savingSettings && <Loader2 className="w-4 h-4 animate-spin" />}
          Save
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Unique volunteers" value={report.unique_volunteers} />
        <Stat label="Total sign-ups" value={report.total_signups} />
        <Stat label="Total hours pledged" value={report.total_hours} />
        <Stat
          label="Overall fill"
          value={`${report.fill.pct}%`}
          sub={`${report.fill.total_filled} / ${report.fill.total_capacity} spots`}
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mt-4">
        <Stat label="Per day" value={report.by_day.length} sub={dayCounts || "—"} />
        <Stat label="Shift changes" value={report.switches} />
        <Stat label="Cancellations" value={report.cancellations} />
        <Stat label="Reminders sent" value={report.reminders_sent ?? 0} />
        <Stat
          label="Shifts still needing people"
          value={report.shifts_fill.filter((s) => s.remaining > 0).length}
        />
      </div>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div>
          <h3 className="flex items-center gap-2 mb-4 text-xs uppercase tracking-[0.24em] text-[#0E6B3A]">
            <CalendarDays className="w-4 h-4" /> By day
          </h3>
          <div className="rounded-2xl border border-[#8E1226]/12 overflow-hidden">
            <Table data-testid="report-by-day-table">
              <TableHeader>
                <TableRow className="border-[#8E1226]/10 hover:bg-transparent">
                  <TableHead className="text-[#6D726A]">Day</TableHead>
                  <TableHead className="text-[#6D726A] text-right">Sign-ups</TableHead>
                  <TableHead className="text-[#6D726A] text-right">Volunteers</TableHead>
                  <TableHead className="text-[#6D726A] text-right">Hours</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.by_day.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center text-[#6D726A] py-10">No sign-ups yet.</TableCell></TableRow>
                ) : (
                  report.by_day.map((d) => (
                    <TableRow key={d.day_label} className="border-[#8E1226]/8">
                      <TableCell className="text-[#8E1226]">{d.day_label}<span className="text-[#6D726A] text-xs block">{d.date}</span></TableCell>
                      <TableCell className="text-right text-[#8E1226]">{d.signups}</TableCell>
                      <TableCell className="text-right text-[#8E1226]">{d.volunteers}</TableCell>
                      <TableCell className="text-right text-[#8E1226]">{d.hours}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <h3 className="flex items-center gap-2 mt-10 mb-4 text-xs uppercase tracking-[0.24em] text-[#0E6B3A]">
            <History className="w-4 h-4" /> When they signed up
          </h3>
          <div className="rounded-2xl border border-[#8E1226]/12 overflow-hidden">
            <Table data-testid="report-timeline-table">
              <TableHeader>
                <TableRow className="border-[#8E1226]/10 hover:bg-transparent">
                  <TableHead className="text-[#6D726A]">Date</TableHead>
                  <TableHead className="text-[#6D726A] text-right">Sign-ups</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.signups_by_date.length === 0 ? (
                  <TableRow><TableCell colSpan={2} className="text-center text-[#6D726A] py-10">No sign-ups yet.</TableCell></TableRow>
                ) : (
                  report.signups_by_date.map((d) => (
                    <TableRow key={d.date} className="border-[#8E1226]/8">
                      <TableCell className="text-[#8E1226]">{d.date}</TableCell>
                      <TableCell className="text-right text-[#8E1226]">{d.count}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        <div>
          <h3 className="flex items-center gap-2 mb-4 text-xs uppercase tracking-[0.24em] text-[#0E6B3A]">
            <Clock className="w-4 h-4" /> Hours per job
          </h3>
          <div className="rounded-2xl border border-[#8E1226]/12 overflow-hidden">
            <Table data-testid="report-by-role-table">
              <TableHeader>
                <TableRow className="border-[#8E1226]/10 hover:bg-transparent">
                  <TableHead className="text-[#6D726A]">Job / Role</TableHead>
                  <TableHead className="text-[#6D726A] text-right">Sign-ups</TableHead>
                  <TableHead className="text-[#6D726A] text-right">Hours</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.by_role.length === 0 ? (
                  <TableRow><TableCell colSpan={3} className="text-center text-[#6D726A] py-10">No sign-ups yet.</TableCell></TableRow>
                ) : (
                  report.by_role.map((r) => (
                    <TableRow key={r.role} className="border-[#8E1226]/8">
                      <TableCell className="text-[#8E1226]">{r.role}</TableCell>
                      <TableCell className="text-right text-[#8E1226]">{r.signups}</TableCell>
                      <TableCell className="text-right text-[#8E1226]">{r.hours}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      {/* Fill rate per shift */}
      <h3 className="flex items-center gap-2 mt-14 mb-4 text-xs uppercase tracking-[0.24em] text-[#0E6B3A]">
        <Gauge className="w-4 h-4" /> Booked vs capacity — per shift
      </h3>
      <div className="rounded-2xl border border-[#8E1226]/12 overflow-hidden">
        <Table data-testid="report-fill-table">
          <TableHeader>
            <TableRow className="border-[#8E1226]/10 hover:bg-transparent">
              <TableHead className="text-[#6D726A]">Day</TableHead>
              <TableHead className="text-[#6D726A]">Role</TableHead>
              <TableHead className="text-[#6D726A]">Time</TableHead>
              <TableHead className="text-[#6D726A]">Booked</TableHead>
              <TableHead className="text-[#6D726A] w-40">Fill</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {report.shifts_fill.map((s, i) => (
              <TableRow key={i} className="border-[#8E1226]/8">
                <TableCell className="text-[#8E1226] whitespace-nowrap">{s.day_label}</TableCell>
                <TableCell className="text-[#8E1226]">{s.role}</TableCell>
                <TableCell className="text-[#6D726A] whitespace-nowrap">{s.time}</TableCell>
                <TableCell className="text-[#8E1226] whitespace-nowrap">
                  {s.taken}/{s.capacity}
                  {s.remaining > 0 && (
                    <span className="ml-2 text-xs text-[#0E6B3A]">{s.remaining} needed</span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <FillBar pct={s.pct} />
                    <span className="text-xs text-[#6D726A] w-9 text-right">{s.pct}%</span>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Activity log */}
      <h3 className="flex items-center gap-2 mt-14 mb-4 text-xs uppercase tracking-[0.24em] text-[#0E6B3A]">
        <History className="w-4 h-4" /> Recent activity (changes &amp; cancellations)
      </h3>
      <div className="rounded-2xl border border-[#8E1226]/12 overflow-hidden">
        <Table data-testid="report-activity-table">
          <TableHeader>
            <TableRow className="border-[#8E1226]/10 hover:bg-transparent">
              <TableHead className="text-[#6D726A]">Action</TableHead>
              <TableHead className="text-[#6D726A]">Volunteer</TableHead>
              <TableHead className="text-[#6D726A]">Details</TableHead>
              <TableHead className="text-[#6D726A]">When</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {events.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="text-center text-[#6D726A] py-10">No activity yet.</TableCell></TableRow>
            ) : (
              events.map((e, i) => {
                const meta = EVENT_META[e.type] || EVENT_META.signup;
                const Icon = meta.icon;
                return (
                  <TableRow key={i} className="border-[#8E1226]/8" data-testid={`activity-row-${e.type}`}>
                    <TableCell>
                      <span className="inline-flex items-center gap-2 text-sm" style={{ color: meta.color }}>
                        <Icon className="w-4 h-4" strokeWidth={1.7} /> {meta.label}
                      </span>
                    </TableCell>
                    <TableCell className="text-[#8E1226]">
                      {e.name}
                      <span className="text-[#6D726A] text-xs block">{e.email}</span>
                    </TableCell>
                    <TableCell className="text-[#6D726A] text-sm">
                      {e.type === "switch" ? (
                        <span className="flex items-center gap-2 flex-wrap">
                          <span className="line-through opacity-70">{slotText(e.from)}</span>
                          <ArrowRight className="w-3.5 h-3.5 text-[#0E6B3A]" />
                          <span className="text-[#8E1226]">{slotText(e.to)}</span>
                        </span>
                      ) : e.type === "cancel" ? (
                        <span className="line-through opacity-70">{slotText(e.from)}</span>
                      ) : (
                        slotText(e.to)
                      )}
                    </TableCell>
                    <TableCell className="text-[#6D726A] text-sm whitespace-nowrap">{fmtTime(e.at)}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
